import './dokumente.css';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtDate, fmtDayMonth, fmtTime, initials, plural } from '../../lib/format';
import { folderLabel } from '../../lib/ordner';
import { useApi } from '../../lib/useApi';
import { letterPath } from '../briefe/labels';
import type { BriefeBarResponse } from '../briefe/types';
import { useKanzlei } from '../context';
import type { DocsOverview } from './types';

// Karte „Dokumente“ auf der Übersicht der Kanzlei (Sam 11.10., Gegenstück zur Karte in der Mandanten-App): Briefe, die
// Mandanten in den letzten 7 Tagen hochgeladen haben (auch erledigte), Dokumente, die auf die Unterschrift des
// Mandanten warten, und was in den letzten 14 Tagen unterschrieben wurde. Je Bereich die ersten fünf, der Rest auf
// Klick. Nur eigene Mandanten bzw. Bereiche, der Admin sieht alle.

const FIRST = 5;

export function DokumenteCard() {
  const navigate = useNavigate();
  const { me } = useKanzlei();
  const docs = useApi<DocsOverview>('/api/kanzlei/docs-overview', { live: ['docs', 'clients'] });
  const letters = useApi<BriefeBarResponse>('/api/kanzlei/briefe?days=7', { live: ['briefe', 'clients'] });
  if (!docs.data || !letters.data) return null;
  const { waiting, signed } = docs.data;
  const ls = letters.data.items;
  const now = Date.now();
  const toClient = (clientId: string) => navigate(`/kanzlei/mandanten/${clientId}/hochladen`);

  return (
    <div className="card docs-ov">
      <div className="ch">
        <h3>Dokumente</h3>
        <span>{me.user.isAdmin ? 'alle Mandanten' : 'Ihre Mandanten'}</span>
      </div>
      <div className="docs-ov-n">
        <div>
          <b className="num">{ls.length}</b>
          <span>{ls.length === 1 ? 'neuer Brief' : 'neue Briefe'} (7 Tage)</span>
        </div>
        <div>
          <b className="num" style={waiting.length ? { color: 'var(--warn)' } : undefined}>
            {waiting.length}
          </b>
          <span>{waiting.length === 1 ? 'wartet' : 'warten'} auf Unterschrift</span>
        </div>
        <div>
          <b className="num">{signed.length}</b>
          <span>unterschrieben (14 Tage)</span>
        </div>
      </div>

      {ls.length > 0 && (
        <Section title="Neue Briefe von Mandanten">
          {ls.map((l) => (
            <div className="task" key={l.id}>
              <span className="av" aria-hidden="true">
                ✉
              </span>
              <div className="tx">
                <b>{l.sender}</b> · {l.clientName}
                <small>
                  📄 {l.name} · {l.fromOffice ? 'eingescannt' : 'hochgeladen'} am {fmtDayMonth(l.createdAt)}
                  {l.createdBy ? ` von ${l.createdBy}` : ''}
                </small>
              </div>
              {l.status === 'erledigt' ? (
                <span className="pill p-ok">erledigt</span>
              ) : l.unseen ? (
                <span className="pill p-info">neu</span>
              ) : (
                <span className="pill p-neu">in Bearbeitung</span>
              )}
              <button type="button" className="btn sm" onClick={() => navigate(letterPath(l.clientId, l.id))}>
                Ansehen
              </button>
            </div>
          ))}
        </Section>
      )}

      {waiting.length > 0 && (
        <Section title="Warten auf Unterschrift">
          {waiting.map((d) => {
            const days = Math.floor((now - new Date(d.uploadedAt).getTime()) / 86_400_000);
            return (
              <div className="task" key={d.id}>
                <span className="av" aria-hidden="true">
                  {initials(d.clientName)}
                </span>
                <div className="tx">
                  <b>{d.title}</b> · {d.clientName}
                  <small>
                    Dokumente › {folderLabel(d.folder)} · hochgeladen am {fmtDate(d.uploadedAt)}
                    {d.uploadedBy ? ` von ${d.uploadedBy}` : ''}
                  </small>
                </div>
                <span className={`pill ${days >= 7 ? 'p-bad' : 'p-warn'}`}>
                  {days < 1 ? 'seit heute' : `seit ${plural(days, 'Tag', 'Tagen')}`}
                </span>
                <button type="button" className="btn sm" onClick={() => toClient(d.clientId)}>
                  Zum Mandanten
                </button>
              </div>
            );
          })}
        </Section>
      )}

      {signed.length > 0 && (
        <Section title="Unterschrieben">
          {signed.map((d) => (
            <div className="task" key={d.id}>
              <span className="av" aria-hidden="true">
                ✓
              </span>
              <div className="tx">
                <b>{d.title}</b> · {d.clientName}
                <small>
                  Unterschrieben von {d.signerName} am {fmtDate(d.signedAt!)}, {fmtTime(d.signedAt!)} Uhr
                </small>
              </div>
              <a className="btn sm" href={`/api/kanzlei/doc/${d.id}/signed`} target="_blank" rel="noopener noreferrer">
                Unterschriebenes PDF
              </a>
            </div>
          ))}
        </Section>
      )}

      {!ls.length && !waiting.length && !signed.length && (
        <p className="note">Keine neuen Briefe in den letzten 7 Tagen, und kein Dokument wartet auf eine Unterschrift.</p>
      )}
    </div>
  );
}

// Überschrift, die ersten fünf Zeilen, Rest auf Klick
function Section({ title, children }: { title: string; children: ReactNode[] }) {
  const [all, setAll] = useState(false);
  const rest = children.length - FIRST;
  return (
    <div className="docs-ov-s">
      <h4>
        {title} <span>{children.length}</span>
      </h4>
      <div>{all ? children : children.slice(0, FIRST)}</div>
      {rest > 0 && (
        <button type="button" className="linkbtn docs-ov-more" aria-expanded={all} onClick={() => setAll((v) => !v)}>
          {all ? 'Weniger anzeigen' : `Alle anzeigen (${children.length})`}
        </button>
      )}
    </div>
  );
}
